import React, { ReactNode, useCallback, useEffect, useRef } from 'react'
import * as ScrollAreaBase from '@radix-ui/react-scroll-area'
import clsx from 'classnames'
import styles from './styles.module.scss'

type ScrollAreaProps = {
  children: ReactNode
  type?: 'auto' | 'always' | 'scroll' | 'hover' | undefined
}

export const ScrollArea = ({ children, type }: ScrollAreaProps) => {
  const gTop = useRef<HTMLDivElement>(null)
  const gBottom = useRef<HTMLDivElement>(null)
  const gLeft = useRef<HTMLDivElement>(null)
  const gRight = useRef<HTMLDivElement>(null)
  const viewport = useRef<HTMLDivElement>(null)

  const onScroll = useCallback(({ currentTarget }: { currentTarget: HTMLDivElement }) => {
    const maxScrollValue = currentTarget.scrollHeight - currentTarget.clientHeight
    const scrollTop = currentTarget.scrollTop
    const scrollBottom = currentTarget.scrollHeight - (currentTarget.scrollTop + currentTarget.clientHeight)

    const scrollTopPercent = maxScrollValue !== 0 ? scrollTop / maxScrollValue : 0
    const scrollBottomPercent = maxScrollValue !== 0 ? scrollBottom / maxScrollValue : 0

    const maxScrollX = currentTarget.scrollWidth - currentTarget.clientWidth
    const scrollLeft = currentTarget.scrollLeft
    const scrollRight = currentTarget.scrollWidth - (currentTarget.scrollLeft + currentTarget.clientWidth)

    const scrollLeftPercent = maxScrollX !== 0 ? scrollLeft / maxScrollX : 0
    const scrollRightPercent = maxScrollX !== 0 ? scrollRight / maxScrollX : 0

    if (gLeft?.current) gLeft.current.style.transform = `scaleX(${scrollLeftPercent})`
    if (gRight?.current) gRight.current.style.transform = `scaleX(${scrollRightPercent})`

    if (gTop?.current) gTop.current.style.transform = `scaleY(${scrollTopPercent})`
    if (gBottom?.current) gBottom.current.style.transform = `scaleY(${scrollBottomPercent})`
  }, [])

  useEffect(() => {
    if (viewport.current) onScroll({ currentTarget: viewport.current })
  }, [children, onScroll])

  return (
    <ScrollAreaBase.Root type={type} className={styles['scroll-area-root']}>
      <ScrollAreaBase.Viewport ref={viewport} onScroll={onScroll} className={styles['scroll-area-viewport']}>
        <div ref={gTop} className={styles['g-top']} />
        <div ref={gBottom} className={clsx(styles['g-bottom'], { [styles.disabled]: type === 'always' })} />
        {children}
      </ScrollAreaBase.Viewport>
      <ScrollAreaBase.Scrollbar
        className={clsx(styles['scroll-area-scrollbar'], { [styles.disabled]: type === 'always' })}
        orientation="vertical"
      >
        <ScrollAreaBase.Thumb className={styles['scroll-area-thumb']} />
      </ScrollAreaBase.Scrollbar>
      <ScrollAreaBase.Scrollbar className={styles['scroll-area-scrollbar']} orientation="horizontal">
        <ScrollAreaBase.Thumb className={styles['scroll-area-thumb']} />
      </ScrollAreaBase.Scrollbar>
      <ScrollAreaBase.Corner className={styles['scroll-area-corner']} />
    </ScrollAreaBase.Root>
  )
}
